// Inline month calendar component for Google Agenda style integration
// CREATED: Inline version of MonthCalendar for seamless integration into MyTasksView
// Features: Month navigation with arrows and swipe, adjacent days display, smooth animations
// Inspired by Google Calendar's inline calendar behavior
// UPDATED: Dynamic week calculation - displays 5 or 6 weeks based on month requirements instead of always 6 weeks

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

interface InlineMonthCalendarProps {
  selectedDate: string;
  onSelectDate: (date: string) => void;
  isOpen: boolean;
}

export function InlineMonthCalendar({ selectedDate, onSelectDate, isOpen }: InlineMonthCalendarProps) {
  const selected = new Date(selectedDate);
  const [currentMonth, setCurrentMonth] = useState(selected);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();

  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  // Adjust to make Monday = 0, Sunday = 6
  const firstDayOfMonthRaw = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();
  const firstDayOfMonth = firstDayOfMonthRaw === 0 ? 6 : firstDayOfMonthRaw - 1;

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  // Swipe handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNextMonth();
    }
    if (isRightSwipe) {
      goToPreviousMonth();
    }
  };

  const handleDayClick = (day: number, monthOffset: number = 0) => {
    const targetMonth = monthOffset !== 0
      ? new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset)
      : currentMonth;

    const year = targetMonth.getFullYear();
    const month = String(targetMonth.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateStr = `${year}-${month}-${dayStr}`;
    onSelectDate(dateStr);
  };

  const isToday = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toDateString() === selected.toDateString();
  };

  // Get last month's info for adjacent days
  const prevMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
  const daysInPrevMonth = new Date(
    prevMonthDate.getFullYear(),
    prevMonthDate.getMonth() + 1,
    0
  ).getDate();

  const days = [];

  // Add previous month's trailing days
  for (let i = firstDayOfMonth - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i;
    days.push(
      <button
        key={`prev-${day}`}
        onClick={() => handleDayClick(day, -1)}
        className="min-h-[40px] min-w-[40px] flex items-center justify-center transition-colors duration-150 ease-out rounded-xl text-sm font-medium text-gray-400 hover:bg-gray-100 hover:text-gray-600 active:bg-gray-200"
        type="button"
      >
        {day}
      </button>
    );
  }

  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    const todayClass = isToday(day);
    const selectedClass = isSelected(day);

    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateString = date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    days.push(
      <button
        key={day}
        onClick={() => handleDayClick(day)}
        className={`relative min-h-[40px] min-w-[40px] flex flex-col items-center justify-center transition-colors duration-150 ease-out rounded-xl text-sm font-medium ${
          selectedClass
            ? 'bg-primary-600 text-white font-bold shadow-lg ring-2 ring-primary-200 ring-offset-1'
            : todayClass
            ? 'text-primary-700 font-bold'
            : 'text-gray-800 hover:bg-gray-100 active:bg-gray-200'
        }`}
        aria-label={selectedClass ? `${dateString} (sélectionné)` : todayClass ? `${dateString} (aujourd'hui)` : dateString}
        aria-pressed={selectedClass}
        type="button"
      >
        <span>{day}</span>
        {/* Today indicator - discrete dot */}
        {todayClass && (
          <span
            className={`absolute bottom-0.5 w-1.5 h-1.5 rounded-full ${
              selectedClass ? 'bg-white' : 'bg-primary-500'
            }`}
            aria-hidden="true"
          />
        )}
      </button>
    );
  }

  // Calculate the number of weeks needed (5 or 6)
  const totalDaysNeeded = firstDayOfMonth + daysInMonth;
  const weeksNeeded = totalDaysNeeded <= 35 ? 5 : 6;
  const totalCellsNeeded = weeksNeeded * 7;

  // Add next month's leading days to fill the grid
  const totalCells = days.length;
  const remainingCells = totalCellsNeeded - totalCells;
  for (let day = 1; day <= remainingCells && day <= 14; day++) {
    days.push(
      <button
        key={`next-${day}`}
        onClick={() => handleDayClick(day, 1)}
        className="min-h-[40px] min-w-[40px] flex items-center justify-center transition-colors duration-150 ease-out rounded-xl text-sm font-medium text-gray-400 hover:bg-gray-100 hover:text-gray-600 active:bg-gray-200"
        type="button"
      >
        {day}
      </button>
    );
  }

  return (
    <div
      className={`overflow-hidden bg-white border-b border-gray-200 transition-all duration-[450ms] ${
        isOpen ? 'max-h-[500px] opacity-100 mb-6 scale-y-100' : 'max-h-0 opacity-0 scale-y-95 -translate-y-2'
      }`}
      style={{
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      role="region"
      aria-label="Calendrier mensuel"
      aria-hidden={!isOpen}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="px-4 py-4">
        {/* Header with month navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={goToPreviousMonth}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 min-w-[40px] min-h-[40px] flex items-center justify-center active:scale-95"
            aria-label="Mois précédent"
            type="button"
          >
            <ChevronLeft size={20} />
          </button>
          <h3 className="text-base font-semibold text-gray-900">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <button
            onClick={goToNextMonth}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 min-w-[40px] min-h-[40px] flex items-center justify-center active:scale-95"
            aria-label="Mois suivant"
            type="button"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Day names */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(name => (
            <div
              key={name}
              className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider"
            >
              {name}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1.5">
          {days}
        </div>
      </div>
    </div>
  );
}
